import { call, put, takeLatest } from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';
import apiService from '../../api/service/apiService';
import type { ApiResponse } from '../../api/service/apiService';
import { ENDPOINTS } from '../../api/endPoints';
import {
  fetchCustomersRequest,
  fetchCustomersSuccess,
  fetchCustomersFailure,
  addCustomerRequest,
  addCustomerSuccess,
  addCustomerFailure,
  uploadCustomersRequest,
  uploadCustomersSuccess,
  uploadCustomersFailure,
} from '../slices/customersSlice';
import type { Customer } from '../slices/customersSlice';

function* fetchCustomersSaga(
  action: PayloadAction<{ restaurantId: string }>
): Generator<any, void, ApiResponse<{ data: Customer[] }>> {
  try {
    const { restaurantId } = action.payload;
    const endpoint = ENDPOINTS.customers;
    
    const response: ApiResponse<any> = yield call(apiService.get, {
      endpoint,
      headers: {
        'restaurant-id': restaurantId,
      },
    });

    const customers = response.data?.data || response.data || [];
    yield put(fetchCustomersSuccess(customers));
  } catch (error: any) {
    yield put(
      fetchCustomersFailure(error.message || 'Failed to fetch customers')
    );
  }
}

function* addCustomerSaga(
  action: PayloadAction<{ restaurantId: string; customerData: any }>
): Generator<any, void, ApiResponse<any>> {
  try {
    const { restaurantId, customerData } = action.payload;
    const endpoint = ENDPOINTS.customers;
    
    // @ts-ignore - apiService types issue
    const response: ApiResponse<any> = yield call(apiService.post, {
      endpoint,
      body: customerData,
      headers: {
        'restaurant-id': restaurantId,
      },
    });

    yield put(addCustomerSuccess());
    // Refetch customers after successful add
    yield put(fetchCustomersRequest({ restaurantId }));
  } catch (error: any) {
    yield put(
      addCustomerFailure(error.message || 'Failed to add customer')
    );
  }
}

function* uploadCustomersSaga(
  action: PayloadAction<{ restaurantId: string; file: File }>
): Generator<any, void, ApiResponse<any>> {
  try {
    const { restaurantId, file } = action.payload;
    const endpoint = `${ENDPOINTS.customers}/upload`;
    
    const formData = new FormData();
    formData.append('file', file);
    
    // @ts-ignore - apiService types issue
    const response: ApiResponse<any> = yield call(apiService.post, {
      endpoint,
      body: formData,
      headers: {
        'restaurant-id': restaurantId,
        // Don't set Content-Type, browser will set it with boundary for FormData
      },
      isFormData: true,
    });

    yield put(uploadCustomersSuccess());
    // Refetch customers after successful upload
    yield put(fetchCustomersRequest({ restaurantId }));
  } catch (error: any) {
    yield put(
      uploadCustomersFailure(error.message || 'Failed to upload customers')
    );
  }
}

export default function* customersSaga() {
  yield takeLatest(fetchCustomersRequest.type, fetchCustomersSaga);
  yield takeLatest(addCustomerRequest.type, addCustomerSaga);
  yield takeLatest(uploadCustomersRequest.type, uploadCustomersSaga);
}
